import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, Plus, Trash2, MoreVertical, UserPlus, Users,
  Clock, CheckCircle2, ArrowRight, X, Loader2, ChevronRight, Expand
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useDepartmentStore, useTeamStore, useUIStore } from '../lib/store';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';

const DepartmentsPage = () => {
  const navigate = useNavigate();
  const { 
    departments, fetchDepartments, createDepartment, deleteDepartment,
    addMemberToDepartment, removeMemberFromDepartment,
    pipeline, fetchPipeline, userTasks, fetchUserTasks, clearPipeline
  } = useDepartmentStore();
  const { teams, fetchTeams, currentTeam, setCurrentTeam } = useTeamStore();
  const { language } = useUIStore();
  const { t } = useTranslation(language);
  
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [showUserTasksDialog, setShowUserTasksDialog] = useState(false);
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newDept, setNewDept] = useState({ name: '', description: '' });
  const [selectedMemberId, setSelectedMemberId] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchTeams();
        await fetchDepartments();
      } catch (error) {
        toast.error(error.message || t('error'));
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (currentTeam) {
      fetchDepartments(currentTeam.id).catch(console.error);
    }
  }, [currentTeam?.id]);

  const handleCreateDepartment = async () => {
    if (!newDept.name.trim() || !currentTeam) {
      toast.error(language === 'hi' ? 'नाम और टीम आवश्यक है' : 'Name and team required');
      return;
    }

    setIsCreating(true);
    try {
      await createDepartment(newDept.name, newDept.description, currentTeam.id);
      toast.success(language === 'hi' ? 'विभाग बनाया गया' : 'Department created');
      setShowCreateDialog(false);
      setNewDept({ name: '', description: '' });
    } catch (error) {
      toast.error(error.message || t('error'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleViewPipeline = async (dept) => {
    setSelectedDept(dept);
    try {
      await fetchPipeline(dept.id);
    } catch (error) {
      toast.error(error.message || t('error'));
    }
  };

  const handleViewUserTasks = async (user) => {
    setSelectedUser(user);
    try {
      await fetchUserTasks(user.id);
      setShowUserTasksDialog(true);
    } catch (error) {
      toast.error(error.message || t('error'));
    }
  };

  const handleAddMember = async () => {
    if (!selectedMemberId || !selectedDept) return;
    
    try {
      await addMemberToDepartment(selectedDept.id, selectedMemberId);
      toast.success(language === 'hi' ? 'सदस्य जोड़ा गया' : 'Member added');
      setShowAddMemberDialog(false);
      setSelectedMemberId('');
      if (selectedDept) {
        await fetchPipeline(selectedDept.id);
      }
    } catch (error) {
      toast.error(error.message || t('error'));
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedDept) return;
    
    try {
      await removeMemberFromDepartment(selectedDept.id, userId);
      toast.success(language === 'hi' ? 'सदस्य हटाया गया' : 'Member removed');
      await fetchPipeline(selectedDept.id);
    } catch (error) {
      toast.error(error.message || t('error'));
    }
  };

  const availableMembers = currentTeam?.members?.filter(
    m => !selectedDept?.member_ids?.includes(m.user_id)
  ) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="departments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6 text-primary" />
            {language === 'hi' ? 'विभाग / कार्य' : 'Departments / Functions'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'hi' ? 'टीम के सदस्यों को विभागों में व्यवस्थित करें' : 'Organize team members into departments'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {teams.length > 0 && (
            <Select
              value={currentTeam?.id || ''}
              onValueChange={(value) => {
                const team = teams.find(t => t.id === value);
                setCurrentTeam(team);
                clearPipeline();
                setSelectedDept(null);
              }}
            >
              <SelectTrigger className="w-[200px] bg-secondary/50 border-white/10">
                <SelectValue placeholder={language === 'hi' ? 'टीम चुनें' : 'Select team'} />
              </SelectTrigger>
              <SelectContent>
                {teams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            onClick={() => setShowCreateDialog(true)}
            disabled={!currentTeam}
            className="glow-primary"
            data-testid="create-dept-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            {language === 'hi' ? 'नया विभाग' : 'New Department'}
          </Button>
        </div>
      </div>

      {!currentTeam ? (
        <Card className="glass">
          <CardContent className="py-16 text-center">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {language === 'hi' ? 'पहले एक टीम चुनें' : 'Please select a team first'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Departments List */}
          <div className="space-y-3">
            <h2 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wider">
              {language === 'hi' ? 'विभाग' : 'Departments'}
            </h2>
            {departments.length === 0 ? (
              <Card className="glass">
                <CardContent className="py-8 text-center">
                  <Building2 className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {language === 'hi' ? 'कोई विभाग नहीं' : 'No departments'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              departments.map((dept) => (
                <Card
                  key={dept.id}
                  className={`glass cursor-pointer card-hover ${
                    selectedDept?.id === dept.id ? 'border-primary/50 bg-primary/10' : ''
                  }`}
                  onClick={() => handleViewPipeline(dept)}
                  data-testid={`dept-card-${dept.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-accent/20 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-accent" />
                        </div>
                        <div>
                          <p className="font-medium">{dept.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {dept.members?.length || 0} {language === 'hi' ? 'सदस्य' : 'members'}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Pipeline View */}
          <div className="lg:col-span-3">
            {pipeline ? (
              <Card className="glass">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-heading">{pipeline.department.name}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {language === 'hi' ? 'टास्क पाइपलाइन - नियत तारीख के अनुसार' : 'Task Pipeline - by Due Date'}
                    </p>
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setSelectedDept(departments.find(d => d.id === pipeline.department.id));
                      setShowAddMemberDialog(true);
                    }}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    {language === 'hi' ? 'सदस्य जोड़ें' : 'Add Member'}
                  </Button>
                </CardHeader>
                <CardContent>
                  {pipeline.pipeline.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        {language === 'hi' ? 'इस विभाग में कोई सदस्य नहीं' : 'No members in this department'}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {pipeline.pipeline.map((userPipeline) => (
                        <div
                          key={userPipeline.user.id}
                          className="bg-secondary/30 rounded-xl p-4"
                        >
                          {/* User Header */}
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarFallback className="bg-primary/20 text-primary">
                                  {userPipeline.user.name?.charAt(0).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-sm">{userPipeline.user.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {userPipeline.task_counts.done}/{userPipeline.total_tasks} {language === 'hi' ? 'पूर्ण' : 'done'}
                                </p>
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleViewUserTasks(userPipeline.user)}>
                                  {language === 'hi' ? 'सभी टास्क देखें' : 'View All Tasks'}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate(`/app/departments/users/${userPipeline.user.id}`)}>
                                  <Expand className="h-4 w-4 mr-2" />
                                  {language === 'hi' ? 'विस्तृत दृश्य' : 'Explode Details'}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleRemoveMember(userPipeline.user.id)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  {language === 'hi' ? 'हटाएं' : 'Remove'}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>

                          {/* Active Tasks */}
                          <ScrollArea className="h-[250px]">
                            <div className="space-y-2">
                              {userPipeline.active_tasks.length === 0 ? (
                                <p className="text-xs text-muted-foreground text-center py-4">
                                  {language === 'hi' ? 'कोई सक्रिय टास्क नहीं' : 'No active tasks'}
                                </p>
                              ) : (
                                userPipeline.active_tasks.map((task) => (
                                  <div
                                    key={task.id}
                                    className="p-2 rounded-lg bg-card/50 border border-white/5"
                                  >
                                    <p className="text-sm font-medium line-clamp-1">{task.title}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                                        task.status === 'in_progress' ? 'bg-chart-4/20 text-chart-4' : 'bg-muted text-muted-foreground'
                                      }`}>
                                        {t(task.status)}
                                      </span>
                                      {task.due_date && (
                                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                                          <Clock className="h-3 w-3" />
                                          {new Date(task.due_date).toLocaleDateString()}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          </ScrollArea>

                          {/* View All Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full mt-3"
                            onClick={() => handleViewUserTasks(userPipeline.user)}
                          >
                            {language === 'hi' ? 'सभी टास्क देखें' : 'View All Tasks'}
                            <ArrowRight className="h-4 w-4 ml-2" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="glass h-full">
                <CardContent className="py-20 text-center">
                  <Building2 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading text-lg font-semibold mb-2">
                    {language === 'hi' ? 'विभाग चुनें' : 'Select a Department'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'hi' ? 'टास्क पाइपलाइन देखने के लिए बाएं से एक विभाग चुनें' : 'Choose a department from the left to view the task pipeline'}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Create Department Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="glass border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {language === 'hi' ? 'नया विभाग' : 'New Department'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{language === 'hi' ? 'विभाग का नाम' : 'Department Name'}</Label>
              <Input
                value={newDept.name}
                onChange={(e) => setNewDept({ ...newDept, name: e.target.value })}
                placeholder={language === 'hi' ? 'जैसे: डिज़ाइन, मार्केटिंग' : 'e.g., Design, Marketing'}
                className="bg-secondary/50 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>{language === 'hi' ? 'विवरण' : 'Description'}</Label>
              <Textarea
                value={newDept.description}
                onChange={(e) => setNewDept({ ...newDept, description: e.target.value })}
                placeholder={language === 'hi' ? 'वैकल्पिक विवरण' : 'Optional description'}
                className="bg-secondary/50 border-white/10"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateDialog(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleCreateDepartment} disabled={isCreating} className="glow-primary">
              {isCreating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {language === 'hi' ? 'बनाएं' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
        <DialogContent className="glass border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {language === 'hi' ? 'सदस्य जोड़ें' : 'Add Member'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{language === 'hi' ? 'सदस्य चुनें' : 'Select Member'}</Label>
              <Select value={selectedMemberId} onValueChange={setSelectedMemberId}>
                <SelectTrigger className="bg-secondary/50 border-white/10">
                  <SelectValue placeholder={language === 'hi' ? 'सदस्य चुनें' : 'Select member'} />
                </SelectTrigger>
                <SelectContent>
                  {availableMembers.length === 0 ? (
                    <SelectItem value="no-members" disabled>
                      {language === 'hi' ? 'कोई उपलब्ध सदस्य नहीं' : 'No available members'}
                    </SelectItem>
                  ) : (
                    availableMembers.map((member) => (
                      <SelectItem key={member.user_id} value={member.user_id}>
                        {member.name} ({member.email})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddMemberDialog(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleAddMember} disabled={!selectedMemberId} className="glow-primary">
              {language === 'hi' ? 'जोड़ें' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Tasks Dialog (Kanban View) */}
      <Dialog open={showUserTasksDialog} onOpenChange={setShowUserTasksDialog}>
        <DialogContent className="glass border-white/10 max-w-5xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {selectedUser?.name} - {language === 'hi' ? 'सभी टास्क' : 'All Tasks'}
            </DialogTitle>
          </DialogHeader>
          
          {userTasks && (
            <div className="grid grid-cols-3 gap-4 overflow-y-auto max-h-[60vh]">
              {['todo', 'in_progress', 'done'].map((status) => (
                <div key={status} className="bg-secondary/30 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`h-2 w-2 rounded-full ${
                      status === 'done' ? 'bg-chart-3' : status === 'in_progress' ? 'bg-chart-4' : 'bg-muted-foreground'
                    }`} />
                    <h4 className="font-medium text-sm">{t(status)}</h4>
                    <span className="text-xs text-muted-foreground">({userTasks.tasks[status]?.length || 0})</span>
                  </div>
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-2">
                      {userTasks.tasks[status]?.map((task) => (
                        <div
                          key={task.id}
                          className="p-3 rounded-lg bg-card/50 border border-white/5"
                        >
                          <p className="font-medium text-sm">{task.title}</p>
                          {task.description && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{task.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              task.priority === 'high' ? 'bg-destructive/20 text-destructive' :
                              task.priority === 'medium' ? 'bg-chart-4/20 text-chart-4' :
                              'bg-muted text-muted-foreground'
                            }`}>
                              {t(task.priority)}
                            </span>
                            {task.due_date && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {new Date(task.due_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowUserTasksDialog(false)}>
              {t('close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DepartmentsPage;
